import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { fetchDealRooms, invalidateDealRoomCreationState, invalidateDealRoomDeletionState, invalidateDealRooms, invalidateDealRoomUpdationState, updateDealRoom } from "src/redux/actions/dealRoom";

import { DealRoom } from "src/types/dealRoom";
import { getSortPreference, persistSortPreference } from "src/utils/dealRoom.helper";

import { CreateDealRoomDialog } from "./components/create";
import { CloneHelpToolbar, CloneMenubar, DealRoomsEditMenuBar, DeleteMenubar, DeleteSelectionToolbar, FilterDropdown, RenameMenubar } from './components/edit/index';
import { DealRoomCard, PlaceholderDealRoomCard } from './components/card/index';
import { SortByAction } from "./components/sortBy";
import EmptyMessage from "src/components/emptyMessage";
import InfiniteScroll from "src/components/infiniteScroll";

import styles from './styles.module.scss';
import _ from "lodash";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import { useDealRoomAdoption } from "src/redux/actions/adoption/dealRoom";
import { DEAL_ROOM, DEAL_ROOMS_LIST } from "src/constants/adoption/sourceTypes";
import { useContentMapAdoption } from "src/redux/actions/adoption/contentMap";
import userRoleService from "src/utils/userRoles.service";
import subscriptionPlanService from "src/utils/subscriptionPlan.service";

interface DealRoomsContextProps {
  sort: { sortBy: string, orderBy: string };
  filterBy: string;
  editMode: 'rename' | 'delete' | 'clone' | null | undefined;
  edits: EditChange[];
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onEditChanges: (value: EditChange) => void;
}

interface EditChange {
  id: string;
  type: 'dealRoom';
  value: string | boolean | DealRoom | null | undefined;
  oldName?: string;
}

export const DealRoomsContext = React.createContext<DealRoomsContextProps>({
  sort: { sortBy: 'name', orderBy: 'asc' },
  filterBy: 'all',
  editMode: null,
  edits: [] as EditChange[],
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onEditChanges: (value: EditChange) => { }
});

export const useDealRoomsState = (filterId : string | undefined, query: string | null) => {
  return useSelector((state) => {
    let dealRoomState = _.get(state, 'dealRooms');

    if (filterId) {
      dealRoomState = _.get(state, `dealRooms.filtered[${filterId}]`);
    }

    const dealRooms = _.get(dealRoomState, 'items') || [];
    const loading = _.get(dealRoomState, 'loading');
    const pagination = _.get(dealRoomState, 'pagination');
    const totalPages = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= totalPages) {
      nextPage = null;
    }

    return {
      dealRooms,
      loading,
      hasMoreItems,
      nextPage,
      totalPages,
      totalRecordsCount
    };
  }, _.isEqual);
}

const DealRooms: React.FC = () => {

  const { filterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('query');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { trackCollectionRenamed } = useCollectionsAdoption();
  const { trackContentSearched } = useContentMapAdoption();
  const { trackDealRoomListViewed, trackDealRoomSearched, trackDealRoomRenamed, trackDealRoomSorted } = useDealRoomAdoption();

  const { totalRecordsCount } = useDealRoomsState(filterId, query);

  const [createDealRoom, setCreateDealRoom] = React.useState(false);
  const [dealRoomsContext, setDealRoomsContext] = React.useState<DealRoomsContextProps>({
    filterBy: 'all',
    sort: getSortPreference(),
    editMode: null,
    edits: [],
    onEditChanges: (value: EditChange) => { }
  });

  useEffect(() => {

    let contextState = {
      editMode: null,
      edits: [],
      onEditChanges: handleEditChanges,
      filterBy: filterId || 'all'
    };
 
    setDealRoomsContext((state) => {
      return {
        ...state,
        ...contextState
      }
    });

    trackDealRoomListViewed();
  }, []);

  const fetchDealRoomsForPage = (page: number) => {

      const request = {
        filter: filterId,
        page,
        limit: 20,
        highlight: false,
        ..._.get(dealRoomsContext, "sort"),
        ...(query ? { query } : {}),
    }
    dispatch(fetchDealRooms(request));

  }

  const handleEditChanges = (value: EditChange) => {
    setDealRoomsContext((state) => {
      const edits = [...state.edits];
      const index = _.findIndex(edits, { id: value.id });
      if (index >= 0) {
        edits[index] = value;
      } else {
        edits.push(value);
      }

      return {
        ...state,
        edits: edits
      }
    });
  }


  const handleEditModeChange = (value: any) => {
    setDealRoomsContext((state) => {
      const edits = value != null ? state.edits : [];

      if (!value) {
        dispatch(invalidateDealRoomUpdationState());
        dispatch(invalidateDealRoomDeletionState());
        dispatch(invalidateDealRoomCreationState());
      }

      return {
        ...state,
        editMode: value,
        edits: edits
      }
    });
  }

  const handleRename = () => {
    const editedDealRooms = _.filter(dealRoomsContext.edits, (edit) => {
      return edit.type === 'dealRoom';
    }).filter(edit => {
      if (edit.value != null && _.size((edit.value as string).trim()) > 0) {
        return true;
      }

      return false;
    });

    editedDealRooms.forEach((edit) => {
      dispatch(updateDealRoom({
        id: edit.id,
        request: { name: (edit.value as string).trim() }
      }));

      trackDealRoomRenamed({
        renameEvent: {
          oldName: edit!.oldName || '',
          newName: edit!.value as string,
        }
      })
    });

    if (_.size(editedDealRooms) == 0) {
      handleEditModeChange(null);
    }
  }


  const handleSearchChange = (value: string | null) => {
    if (value) {
      setSearchParams({ query: value });
      trackContentSearched({
        sourceCta: DEAL_ROOMS_LIST,
        searchString: value,
        filter: DEAL_ROOM,
        searchTags: [],
        categories: []
      });
    } else {
      searchParams.delete('query');
      setSearchParams(searchParams);
    }

  }

  const handleSortChange = (value: any) => {
    setDealRoomsContext((state) => {
      return {
        ...state,
        sort: value
      }
    });

    const existingSortPreference = getSortPreference();
    
    if (
      _.get(existingSortPreference, "sortBy", "") !== _.get(value, "sortBy", "")
    ) {
      trackDealRoomSorted({ sortType: value.sortBy });
    }
      
    persistSortPreference(value.sortBy, value.orderBy);
  }


  const handleFilterChange = (value: string) => {

    const url = value === "all" ? '/deal-rooms' : `/deal-rooms/${value}`;
    navigate(url);

    setDealRoomsContext((state) => {
      return {
        ...state,
        filterBy: value
      }
    });

  }

  if (!userRoleService.canAccessDealRoom()){
    return <div className={styles.loading}>Access Denied</div>
  }

  return (
    <DealRoomsContext.Provider value={dealRoomsContext}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.pageHeader}>
            <h1>deal rooms</h1>
            <p>your deal's command center.</p>
          </div>

          <div className={styles.actionMenu}>
            <DealRoomsEditMenuBar show={!dealRoomsContext.editMode}
              query={query}
              handleSearchChange={handleSearchChange}
              handleEditModeChange={handleEditModeChange}
              setCreateDealRoom={setCreateDealRoom}
            />
            <RenameMenubar show={dealRoomsContext.editMode === 'rename'}
              onConfirm={handleRename}
              onCancel={() => handleEditModeChange(null)}
            />
            <DeleteMenubar show={dealRoomsContext.editMode === 'delete'}
              onCancel={() => {
                handleEditModeChange(null)}
              }
              resetEdits={() => {
                setDealRoomsContext((state) => {
                  return {
                    ...state,
                    edits: []
                  }
                });
              }}
            />
            <CloneMenubar show={dealRoomsContext.editMode === 'clone'}
                onCancel={() => handleEditModeChange(null)}
            />
          </div>
        </div>

        <div className={styles.secondaryMenu}>
          <FilterDropdown totalRecordsCount={totalRecordsCount} filterBy={dealRoomsContext.filterBy} handleFilterChange={handleFilterChange} />

          <div className={styles.sortMenu}>
            <SortByAction value={dealRoomsContext.sort} onChange={handleSortChange} />
          </div>
        </div>

        <DealRoomsList setCreateDealRoom={setCreateDealRoom} fetchDealRoomsForPage={fetchDealRoomsForPage} />
        <DeleteSelectionToolbar
          show={dealRoomsContext.editMode === 'delete'}
          dealRooms={dealRoomsContext.edits.filter((edit) => edit.value).map((edit) => edit.value as DealRoom)}
          onFinish={() => {
            handleEditModeChange(null);
            fetchDealRoomsForPage(0);
          }}
        />
        <CloneHelpToolbar show={dealRoomsContext.editMode === 'clone'} />
        <CreateDealRoomDialog show={createDealRoom} onClose={() => setCreateDealRoom(false)} />
        
      </div>
    </DealRoomsContext.Provider>
  )
}

interface IDealListProps{
  setCreateDealRoom: (value: boolean) => void;
  fetchDealRoomsForPage: (page: number) => void;
}

const DealRoomsList: React.FC<IDealListProps> = ({setCreateDealRoom, fetchDealRoomsForPage}) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasSearchRouteQuery = _.get(useLocation(),'search');
  const { sort, onEditChanges, edits } = React.useContext(DealRoomsContext);
  
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');
  const { filterId } = useParams();

  const { loading, hasMoreItems, dealRooms, nextPage } = useDealRoomsState(filterId, query);

  const { trackCreateDealRoomClicked } = useDealRoomAdoption();

  const hasDealRoomAccess = subscriptionPlanService.getViewerTypeList().includes('DEAL_ROOM');

  useEffect(() => {
    dispatch(invalidateDealRooms());
    fetchDealRoomsForPage(0);
  }, [filterId, sort, query])

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchDealRoomsForPage(nextPage);
    }
  }

  const handleDealRoomClick = (collection: DealRoom) => {
    navigate(`/deal-room/${collection.id}/overview`);
  }

  return (
    <div className={styles.list}>

      <InfiniteScroll className={styles.cards}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          return _.times(_.size(dealRooms) == 0 ? 4 : 1).map((i) => (
            <PlaceholderDealRoomCard isFirstCard={ _.size(dealRooms) === 0 && i === 0} key={i} />
          ));
        }}
      >

        {dealRooms.map((dealRoom: DealRoom, i) => {
          const edit = (edits || []).find((edit) => edit.id === _.get(dealRoom, 'id'));
          const selected = _.get(edit, 'value') || false;
          return <DealRoomCard 
            isFirstCard={i === 0}
            selected={!!selected} item={dealRoom} key={dealRoom.id}
            onEditChanges={onEditChanges}
            onClick={handleDealRoomClick}
          />
        })}

      </InfiniteScroll>

      {_.size(dealRooms) === 0 && !loading && <div className={styles.emptyMessage}>
        <EmptyMessage
          width={400}
          image={hasSearchRouteQuery ? '/images/assets/assetsEmptyMessage.svg' : '/images/dealRoom/empty.svg'}
          heading={hasSearchRouteQuery ? "No results found" : "No deal rooms yet. Create your first one." }
          action={
            userRoleService.canShare() && userRoleService.canCreateDealRoom() && hasDealRoomAccess
              ? {
                  title: "Create new",
                  icon: 'paperflite-add',
                  onClick: () => {
                    setCreateDealRoom(true);
                    trackCreateDealRoomClicked();
                  }
                }
              : undefined
          }
        />
      </div>}
    </div>
  )
}

export default DealRooms;